import { ReadAllResponse, uuid } from "@/interfaces";
import { Service } from "@/models/services.model";
import type { Service as CatalogService, ServicePriceChange } from "@shyne/shared/api";
import { activeOrganizationId, activeOrganizationIdIfAny } from "@/lib/active-organization";
import { api } from "@/lib/api";
import { CatalogRepository } from "@/repositories/catalog.repository";
import { serviceCreateRequestOf, type NewService } from "@/repositories/service-records";

export const toServiceModel = (service: CatalogService): Service => ({
  id: service.id,
  name: service.name,
  image: null,
  price: service.price ?? null,
  payment: service.defaultPay ?? null,
  priced_per_job: service.pricedPerJob ?? false,
  meta_data: null,
  parent: service.parent?.id ?? null,
  organization: service.vendor.id,
  created_at: "",
  updated_at: "",
});

export const ServicesRepository = {
  async getAll(): Promise<ReadAllResponse<Service>> {
    const vendor = activeOrganizationId();
    const services = await CatalogRepository.every({ actingOrganization: vendor, vendor: [vendor] });
    return { data: services.map(toServiceModel), count: services.length };
  },
  async create(service: NewService): Promise<Service> {
    return toServiceModel(await api.services.create(serviceCreateRequestOf(activeOrganizationId(), service)));
  },
  async setPrices(prices: readonly ServicePriceChange[]): Promise<Service[]> {
    const { items } = await api.services.setPrices({ actingOrganization: activeOrganizationId(), prices: [...prices] });
    return items.map(toServiceModel);
  },
  async delete(id: uuid): Promise<void> {
    await api.services.delete(id);
  },

  async getParentServices(): Promise<ReadAllResponse<CatalogService>> {
    const vendor = activeOrganizationIdIfAny();
    if (vendor === undefined) return { data: [], count: 0 };
    const parents = await CatalogRepository.parentsOf(vendor);
    return { data: parents, count: parents.length };
  },
};
